"use client";

import { useState } from "react";
import clsx from "clsx";
import type { ScenarioScreenCheckpoint } from "@/lib/presenter/types";

/**
 * Presenter-chrome subcomponent: L2 director-mode timeline scrubber.
 * Renders the active scenario's screen checkpoints as a 6-col grid;
 * the current screen glows. Click a marker to /control/seek there.
 * Backed by the M5 reset-replay protocol so seek-back is projection-safe.
 *
 * Collapsible — `▾` toggles to `▸` to compact the panel during a long
 * demo so the chrome stays inside the projector viewport.
 *
 * Hidden when there's no active scenario / catalogue not loaded /
 * fewer than 2 screens.
 */
export function TimelineScrubber({
  currentScreen,
  screens,
  onSeek,
}: {
  currentScreen?: string;
  screens: ScenarioScreenCheckpoint[];
  onSeek: (offsetMs: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  if (!screens || screens.length < 2) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="mt-3 flex w-full items-center justify-between text-orbitra-teal-soft uppercase tracking-[0.18em] hover:text-state-verified"
        aria-expanded={!collapsed}
      >
        <span>Timeline · {screens.length} screens · click to seek</span>
        <span aria-hidden="true" className="text-[10px]">{collapsed ? "▸" : "▾"}</span>
      </button>
      {!collapsed && (
        <div className="mt-1 grid grid-cols-6 gap-1">
          {screens.map((s) => {
            const active = s.screen === currentScreen;
            return (
              <button
                key={s.screen}
                type="button"
                onClick={() => onSeek(s.offsetMs)}
                title={`${s.screen} · ${s.label}`}
                className={clsx(
                  "rounded px-1 py-0.5 text-center text-[9px] transition-colors",
                  active
                    ? "bg-state-verified text-orbitra-ink shadow-[0_0_12px_var(--state-verified-glow)]"
                    : "border border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted hover:border-state-verified/50 hover:text-state-verified",
                )}
              >
                {s.screen}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
